import { QueryStatus } from "@reduxjs/toolkit/dist/query"
import { MutableRefObject, useCallback, useRef } from "react"
import {
  useLazyValidateNMLSQuery,
  useLazyValidateEmailQuery,
  useLazyFetchSubscriptionDetailsQuery,
  useLazyFetchSubscriptionMatchesQuery
} from "../../redux/queries"
import {
  CreateSubscriptionPayload,
  LoanOfficer,
  SubscriptionDetails
} from "../../types"
import { normalizePhone } from "../utils"

interface FieldValidationCache {
  lastValue: string
  lastBrandId: string
  lastErrors: Partial<CreateSubscriptionPayload>
  timeout: NodeJS.Timeout | null
  pending: Promise<Partial<CreateSubscriptionPayload>> | null
}

const createFieldCache = (): FieldValidationCache => ({
  lastValue: "",
  lastBrandId: "",
  lastErrors: {},
  timeout: null,
  pending: null
})

export const useValidateEmailAndNMLS = () => {
  const [lazyValidateNMLS] = useLazyValidateNMLSQuery()
  const [lazyValidateEmail] = useLazyValidateEmailQuery()
  const [lazyFetchSubscriptionDetailsQuery] =
    useLazyFetchSubscriptionDetailsQuery()
  const [lazyFetchSubscriptionMatchesQuery] =
    useLazyFetchSubscriptionMatchesQuery()

  const emailCacheRef = useRef<FieldValidationCache>(createFieldCache())
  const phoneCacheRef = useRef<FieldValidationCache>(createFieldCache())

  const lastValidatedNMLSRef = useRef<string>("")
  const lastValidatedLOEmailRef = useRef<string>("")
  const lastValidatedExcludeIdRef = useRef<string | undefined>("")
  const lastLOErrorsRef = useRef<Partial<LoanOfficer>>({})

  const validateSubscriptionField = useCallback(
    (
      field: "email" | "phone",
      normalizedValue: string,
      values: CreateSubscriptionPayload | SubscriptionDetails,
      brandId: string,
      cache: MutableRefObject<FieldValidationCache>
    ): Promise<Partial<CreateSubscriptionPayload>> => {
      const c = cache.current

      if (normalizedValue === c.lastValue && brandId === c.lastBrandId) {
        if (c.pending) return c.pending
        return Promise.resolve(c.lastErrors)
      }

      if (c.timeout) clearTimeout(c.timeout)

      const p = new Promise<Partial<CreateSubscriptionPayload>>(resolve => {
        c.timeout = setTimeout(async () => {
          const errors: Partial<CreateSubscriptionPayload> = {}

          c.lastValue = normalizedValue
          c.lastBrandId = brandId

          if (normalizedValue) {
            const queryArg = { [field]: normalizedValue, brand_id: brandId }
            const { data, status } =
              field === "phone"
                ? await lazyFetchSubscriptionMatchesQuery(queryArg)
                : await lazyFetchSubscriptionDetailsQuery(queryArg)

            if (status === QueryStatus.fulfilled) {
              const conflicting =
                field === "phone"
                  ? Array.isArray(data)
                    ? data.find(
                        d =>
                          (d as any)?.phone &&
                          normalizePhone(String((d as any).phone)) ===
                            normalizedValue &&
                          (d as any)?.lead_id &&
                          (d as any)?.lead_id !== (values as any)?.lead_id
                      )
                    : undefined
                  : (data as any)?.lead_id &&
                    (data as any)?.lead_id !== values.lead_id
                  ? data
                  : undefined

              if (conflicting) {
                const firstName = (conflicting as any).first_name
                const lastName = (conflicting as any).last_name
                errors[
                  field
                ] = `This ${field} is already in use by ${firstName} ${lastName}`
                c.lastErrors = errors
                resolve(errors)
                return
              }
            }
          }

          c.lastErrors = errors
          c.pending = null
          resolve(errors)
        }, 500)
      })
      c.pending = p
      return p
    },
    [lazyFetchSubscriptionDetailsQuery, lazyFetchSubscriptionMatchesQuery]
  )

  const validateLO = useCallback(
    async (values: LoanOfficer) => {
      const excludeId = values.uid || values.loid
      const isNotActiveError =
        "This profile is created but inactive. Email support@bankingbridge.com to activate the account."

      const nmlsValue = values.nmls?.trim() || ""
      const emailValue = values.email?.trim() || ""

      const nmlsChanged =
        nmlsValue !== lastValidatedNMLSRef.current ||
        excludeId !== lastValidatedExcludeIdRef.current
      const emailChanged =
        emailValue !== lastValidatedLOEmailRef.current ||
        excludeId !== lastValidatedExcludeIdRef.current

      if (!nmlsChanged && !emailChanged) {
        return lastLOErrorsRef.current
      }

      const errors: Partial<LoanOfficer> = { ...lastLOErrorsRef.current }

      if (nmlsValue.length > 0 && nmlsChanged) {
        const { data, status } = await lazyValidateNMLS({
          nmls: nmlsValue,
          excludeId
        })

        lastValidatedNMLSRef.current = nmlsValue
        lastValidatedExcludeIdRef.current = excludeId

        if (status === QueryStatus.fulfilled && data.conflictingLo) {
          if (!data.conflictingLo?.active) errors.nmls = isNotActiveError
          else if (
            data.conflictingLo.loid !== values.uid &&
            data.conflictingLo.loid !== values.loid
          )
            errors.nmls = `This NMLS is already in use by ${data.conflictingLo.name} (${data.conflictingLo.bank})`
        } else {
          delete errors.nmls
        }
      } else if (nmlsValue.length === 0) {
        delete errors.nmls
      }

      if (emailValue.length > 0 && emailChanged) {
        const { data, status } = await lazyValidateEmail({
          email: emailValue,
          excludeId
        })

        lastValidatedLOEmailRef.current = emailValue
        lastValidatedExcludeIdRef.current = excludeId

        if (status === QueryStatus.fulfilled && data.conflictingLo) {
          if (!data.conflictingLo?.active) errors.email = isNotActiveError
          else if (
            data.conflictingLo.uid !== values.uid &&
            data.conflictingLo.uid !== values.loid
          )
            errors.email = `This email is already in use by ${data.conflictingLo.name} (${data.conflictingLo.bank})`
        } else {
          delete errors.email
        }
      } else if (emailValue.length === 0) {
        delete errors.email
      }

      lastLOErrorsRef.current = errors
      return errors
    },
    [lazyValidateNMLS, lazyValidateEmail]
  )

  const validateSubscriptionEmail = useCallback(
    (
      values: CreateSubscriptionPayload | SubscriptionDetails,
      brand_id: string
    ) =>
      validateSubscriptionField(
        "email",
        values.email.trim(),
        values,
        brand_id,
        emailCacheRef
      ),
    [validateSubscriptionField]
  )

  const validateSubscriptionPhone = useCallback(
    (
      values: CreateSubscriptionPayload | SubscriptionDetails,
      brand_id: string
    ) =>
      validateSubscriptionField(
        "phone",
        normalizePhone(values.phone ?? ""),
        values,
        brand_id,
        phoneCacheRef
      ),
    [validateSubscriptionField]
  )

  return { validateLO, validateSubscriptionEmail, validateSubscriptionPhone }
}
